import Vue from 'vue';
import _ from 'lodash';
import moment from 'moment';
import EventBus from '@/buses/EventBus';
import PlayerService from '@/services/PlayerService';

const contructInteractionStateFromInteraction = (context, interaction, interactionAttempt) => {
  let constructedInteraction = constructInteraction(context, interaction);
  let constructedInteractionAttempt = constructInteractionAttempt(context, constructedInteraction, interactionAttempt);
  let constructedInteractionMeta = constructInteractionMeta(context, constructedInteraction, constructedInteractionAttempt);
  return {
    interaction: constructedInteraction,
    interactionAttempt: constructedInteractionAttempt,
    interactionMeta: constructedInteractionMeta
  };
}

const constructInteraction = ({state, getters}, interaction) => {
  let constructedInteraction = _.assign({}, _.find(getters.interactions, i => i.id === interaction.id) || _.cloneDeep(state.defaultInteraction), interaction);
  if (
    _.has(constructedInteraction, 'random')
    && constructedInteraction.random
    && [
      'multiple_choice',
      'check_all_apply',
      'poll_q',
    ].includes(constructedInteraction.interactionType)
  ) {
    constructedInteraction.interactionOptions = _.shuffle(constructedInteraction.interactionOptions)
  } else {
    constructedInteraction.interactionOptions = _.sortBy(constructedInteraction.interactionOptions, 'order');
  }
  constructedInteraction.interactionOptions.forEach(io => {
    if (io.jump) io.jump = parseFloat(io.jump);
  });
  return constructedInteraction;
}

const constructInteractionAttempt = ({state, rootGetters}, interaction, interactionAttempt) => {
  const constructedInteractionAttempt = _.assign({}, _.cloneDeep(state.defaultInteractionAttempt), interactionAttempt || {});
  if (!_.has(constructedInteractionAttempt, 'interactionId')) constructedInteractionAttempt.interactionId = interaction.id;
  if (!_.has(constructedInteractionAttempt, 'pointsAvailable')) constructedInteractionAttempt.pointsAvailable = interaction.pointsAvailable;
  if (!_.has(constructedInteractionAttempt, 'bulbAttemptId')) constructedInteractionAttempt.bulbAttemptId = rootGetters['player/bulbAttempt'].id;
  if (typeof constructedInteractionAttempt.displayTime !== 'number') constructedInteractionAttempt.displayTime = parseFloat(constructedInteractionAttempt.displayTime);
  if (!_.has(constructedInteractionAttempt, 'timeElapsed')) constructedInteractionAttempt.timeElapsed = 0;
  if (!_.isArray(constructedInteractionAttempt.interactionOptionIds)) {
    try {
      constructedInteractionAttempt.interactionOptionIds = constructedInteractionAttempt.interactionOptionIds.split(',');
      if (interaction.interactionType === 'short_answer') {
        constructedInteractionAttempt.interactionOptionIds = interaction.interactionOptions.map(io => io.id);
      }
    } catch(e) {
      constructedInteractionAttempt.interactionOptionIds = [];
    }
  }
 constructedInteractionAttempt.interactionOptionIds = constructedInteractionAttempt.interactionOptionIds.map(ioi => _.parseInt(ioi));
  if (!_.isArray(constructedInteractionAttempt.responseText)) {
    if (constructedInteractionAttempt.responseText) { // ia has responseText
      // split as long as it isn't a free response
      if (interaction.interactionType === 'short_answer') {
        const delimiter = interaction?.config?.hasNewFillInTheBlankDelimiter
          ? '^||*|||*||^'
          : ',';
        constructedInteractionAttempt.responseText = interactionAttempt.responseText.split(delimiter);
      } else if (interaction.interactionType === 'free_response') {
        constructedInteractionAttempt.responseText = [interactionAttempt.responseText] // free response should not be split
      } else {
        constructedInteractionAttempt.responseText = interactionAttempt.responseText.split(',');
      }
    }
     else {
       constructedInteractionAttempt.responseText = [];
     }
  }
  return constructedInteractionAttempt;
}

const constructInteractionMeta = ({state, getters}, interaction, interactionAttempt, meta) => {

  const icons =  { // question type mapped to material icon name
    multiple_choice: 'format_list_bulleted',
    check_all_apply: 'playlist_add_check',
    free_response: 'border_color',
    skip_to: 'content_cut',
    short_answer: 'subtitles',
    pause_q: 'bookmark_border',
    iframe_q: 'public',
    file_viewer_q: 'picture_as_pdf',
    poll_q: 'poll',
    discussion_q: 'chat',
    default: 'pets',
    info: 'info',
  }
  let interactionMeta = {
    pulse: _.has(meta, 'pulse') ? meta.pulse : false,
    inDisplayRange: _.has(meta, 'inDisplayRange') ? meta.inDisplayRange : false,
    viewed: _.has(meta, 'viewed') ? meta.viewed : false,
    initialize: _.has(meta, 'initialize') ? meta.initialize : false,
    overlay: (() => {
      if (_.has(meta, 'overlay')) return meta.overlay; // uses existing meta
      if (_.has(interaction, 'position.position_type')) return interaction.position.position_type !== 'sidebar'; // if type === sidebar, overlay is false
      else return false;// default to sidebar
    })(),
    rubric: _.has(interaction.config, 'template_key') ? interaction.config.template_key === 'rubric' : false,
    collapsed: _.has(meta, 'collapsed') ? meta.collapsed : false,
    expanded: _.has(meta, 'expanded') ? meta.expanded: false,
    hiddenByRequired: _.has(meta, 'hiddenByRequired') ? meta.hiddenByRequired : true,
    icon: icons[interaction.interactionType],
    interactionId: interaction.id,
    isInteractionAnswered: interactionAttempt && (interactionAttempt.id || interactionAttempt.graded) ? true : false,
    isInteractionAutograded: (() => {
      return ['multiple_choice', 'check_all_apply', 'short_answer'].includes(interaction.interactionType);
    })(),
    individualAnswerStatuses: (() => {
      if (_.has(interactionAttempt, 'postSubmission.individualAnswerStatuses') && _.isObject(interactionAttempt.postSubmission.individualAnswerStatuses)) return interactionAttempt.postSubmission.individualAnswerStatuses;
      return {};
    })(),
    correctAnswers: (() => {
      if (_.has(interactionAttempt, 'postSubmission.correctAnswers') && _.isArray(interactionAttempt.postSubmission.correctAnswers)) return interactionAttempt.postSubmission.correctAnswers;
      return [];
    })(),
    isAnswerCorrect: (() => {
      if (_.has(interactionAttempt, 'postSubmission.isAnswerCorrect')) return interactionAttempt.postSubmission.isAnswerCorrect;
      // what about quesitons that don't have any answers
      if (
        interactionAttempt &&
        (interactionAttempt.id || interactionAttempt.graded) &&
        _.isNumber(interactionAttempt.pointsEarned)
      ) {
        return (interactionAttempt.pointsEarned === interaction.pointsAvailable);
      } else {
        return false;
      }
    })(),
    isAnswerEmpty: (() => {
      if (['multiple_choice', 'poll_q', 'check_all_apply'].includes(interaction.interactionType)) {
        return !(interactionAttempt && interactionAttempt.interactionOptionsIds && interactionAttempt.interactionOptionsIds.length);
      } else if (['free_response'].includes(interaction.interactionType)) {
        return !(interactionAttempt && interactionAttempt.responseText.length > 0)
      }
      return false;
    })(),
    isRetakeExplanation: (interactionAttempt && (interactionAttempt.id || interactionAttempt.graded)) ? true : false,
    isExplanationSubmitted: (() => {
      if (_.has(interaction, 'config.template_key') && interaction.config.template_key === 'rubric') {
        return Boolean(interactionAttempt && (interactionAttempt.id || interactionAttempt.graded));
      }
      return interactionAttempt &&
        (interactionAttempt.id || interactionAttempt.graded) &&
        interactionAttempt.explanation &&
        interactionAttempt.explanation.trim().length > 0;
    })(),
    isRecordingAudio: false,
    isModalOpen: false,
    isProcessingData: false,
    isProcessingSecondaryData: false,
    attempts: _.has(meta, 'attempts') ? meta.attempts : 0,
    sessionStartTime: _.has(meta, 'sessionStartTime') ? meta.sessionStartTime : 0,
    tempSessionTime: _.has(interactionAttempt, 'timeElapsed') ? interactionAttempt.timeElapsed : 0,
    useRichText: false,
    darkMode: ['multiple_choice', 'check_all_apply', 'free_response', 'short_answer', 'pause_q', 'poll_q', 'discussion_q'].includes(interaction.interactionType) && _.get(interaction, 'position.dark', false),
    opacity: _.get(interaction, 'position.opacity', 1),
    pastSessionTime: false,
    pastInteractionOffset: false,
  };


  Vue.delete(interactionMeta, 'postSubmission');
  return interactionMeta;
}

const constructNotes = () => {
  return  {
    interaction: {
      id: 'notes',
      interactionText: 'Notes',
      interactionType: 'notes_q',
    },
    interactionMeta: {
      initialize: true
    }
  };
}

const constructTranscript = () => {
  return  {
    interaction: {
      id: 'transcript',
      interactionText: 'Transcript',
      interactionType: 'transcript',
    },
    interactionMeta: {
      initialize: true
    }
  };
}
const requestSetActiveInteraction = (context, request) => {
	// determine requested interaction:
  if (!request) { // request to set the activeInteraction to null
		context.commit('updateActiveInteraction', null);
		if (context.rootGetters['player/closeBulbInfo']) {
		  context.commit('player/setCloseBulbInfo', false, {root: true});
		  context.commit('player/setSidebarOpen', false, {root: true});
		}
		context.commit('player/setShowInteractionsTab', false, {root: true});
	}
	else if (request === 'next') {
		const sidebarInteractions = _.filter(context.getters.activeInteractionGroup, aig => !aig.interactionMeta.overlay && !aig.interactionMeta.hiddenByRequired);
		if (!context.getters.activeInteraction && sidebarInteractions.length) {
			// there is no activeInteraction but there are interactions in the sidebar group
			  context.commit('player/openInteractionsTab', null, {root: true});
				context.commit('updateActiveInteraction', sidebarInteractions[0].interaction)
		} else {
			// find index of the current active interaction
			const activeIndex = _.findIndex(sidebarInteractions, si => si.interaction.id === context.getters.activeInteraction.id)
			// as long as it's not the last interaction in the array set the activeInteraction to be index + 1
			if (activeIndex !== sidebarInteractions.length - 1) {
			  context.commit('player/openInteractionsTab', null, {root: true});
				context.commit('updateActiveInteraction', sidebarInteractions[activeIndex + 1].interaction);
			}
		}
	}
	else if (request === 'previous') {
		const sidebarInteractions = _.filter(context.getters.activeInteractionGroup, aig => !aig.interactionMeta.overlay && !aig.interactionMeta.hiddenByRequired)
		const activeIndex = _.findIndex(sidebarInteractions, si => si.interaction.id === context.getters.activeInteraction.id)
		if (activeIndex > 0) {
			context.commit('player/openInteractionsTab', null, {root: true});
			context.commit('updateActiveInteraction', sidebarInteractions[activeIndex - 1].interaction);

		}
	}
	else if (request) { // interaction passed with request
	  if (['top-left', 'top-right', 'bottom-left', 'bottom-right', 'left', 'right', 'top', 'bottom', 'middle', 'custom'].includes(_.get(request, 'position.position_type'))) {
	    const activeHotspots = context.getters.activeOverlayInteractions;
	    // loop through hot spots and collapse any before selected
	    let collapseHotspot = true;
	    for (const hotspot of activeHotspots) {
	      if (hotspot.interaction.id === request.id) {
	        collapseHotspot = false;
	      }
	      context.commit('collapseHotspot', { interactionId: hotspot.interaction.id, value: collapseHotspot });
	    }
	    return; // Hotspot can never be sent to the sidebar
	  }
		const meta = context.getters.interactionMeta(request.id);
	  // this will prevent setting interactions outside of the display time if seeking is disallowed
	  // unless userType = teacher && playbackMode = instructor
		if (!meta.inDisplayRange && (!context.rootGetters['player/allowRewind'] || !_.get(context, 'rootState.player.bulb.config.fast_yes', false))) return;
		if (!meta.hiddenByRequired) {
			context.commit('player/openInteractionsTab', null, {root: true});
			context.commit('updateActiveInteraction', request);
		}
	}
};

const doesDiscussionInteractionHaveCommentReplies = comments => {
  return !!comments.find( comment => comment?.replies.length > 0);
};

export default {
  requestSetActiveInteraction,
  constructAnswerSubmission: async function(context, {interaction = {}, interactionAttempt = {}, interactionMeta = {}, displayTime = 0}) {
    interactionAttempt.displayTime = displayTime;
    let bulbAttempt = {}
    if (context.rootGetters['player/bulbAttempt'].id) {
      bulbAttempt = context.rootGetters['player/bulbAttempt'];
      bulbAttempt.timeElapsed = await context.dispatch('player/sessionTime', null, {root: true})
      return { bulbAttempt, interaction, interactionAttempt, interactionMeta, };
    }
    return { interaction, interactionAttempt, interactionMeta, };
  },
  buildInteractionGroups(context, interactions) {
    // used for timeline dot generation
  	const groups = [];
  	let group = {};
  	_.sortBy(interactions, ['displayInTime']).forEach(i => {
  	  if (!['secondary_stream'].includes(i.interactionType)) { // prevent dual streams from generating dots
    		if (_.has(group, 'start')) {
    			if (i.displayInTime - group.last >= .026) {
    				groups.push(_.cloneDeep(group));
    				group = {start: i.displayInTime, last: i.displayInTime, count: 1};
    			} else {
    				group.last = i.displayInTime;
    				group.count += 1;
    			}
    		} else {
    			// first of the loops
    			group = {start: i.displayInTime, last: i.displayInTime, count: 1};
    		}
  	  }
  	})
  	groups.push(group);
  	context.commit('setInteractionGroups', groups);
  	return groups;
  },
  handleGradedAnswer(context, {interaction, interactionAttempt}) {
    // grab the existing meta data
    const meta = _.find(context.getters.interactionMetas, {'interactionId': interaction.id});
    meta.attempts += 1;
    interactionAttempt = constructInteractionAttempt(context, interaction, interactionAttempt, meta);
    const interactionMeta = constructInteractionMeta(context, interaction, interactionAttempt, meta);

    context.commit('upsertInteraction', interaction);
    context.commit('upsertInteractionAttempt', interactionAttempt);
    context.commit('upsertInteractionMeta', interactionMeta);
    // update bulbAttempt with new points value shown to the use
    context.commit('player/updateBulbAttemptPointsEarned', _.reduce(context.getters.interactionAttempts, (sum, ia) => sum + (ia.pointsEarned || 0), 0), { root: true });
    if (context.rootGetters['player/hasNewInteractionDisplayAccess']) context.commit('setInteractionStates');
    return {
      interaction,
      interactionAttempt,
      interactionMeta
    };
  },
  constructInteractionAttempt(context, {interaction, interactionAttempt}) {
    context.commit('upsertInteractionAttempt', constructInteractionAttempt(context, interaction, interactionAttempt));
  },
  constructInitialInteractionStates(context, {interactions = [], interactionAttempts = []}) {
    const constructedInteractionStates = [];
    interactions.forEach(i => constructedInteractionStates.push(contructInteractionStateFromInteraction(context, i, interactionAttempts.find(ia => ia.interactionId === i.id))));

    context.commit('updateAllInteractions', constructedInteractionStates.map(cis => cis.interaction));
    context.commit('updateAllInteractionAttempts', constructedInteractionStates.map(cis => cis.interactionAttempt));
    context.commit('updateAllInteractionMetas', constructedInteractionStates.map(cis => cis.interactionMeta));
  },
  // ####### start of dummy comment actions #######
  loadDummyDiscussionComments(context, {interactionId}) {
    Vue.set(context.state.discussionComments, interactionId, []);
  },
  createDummyDiscussionComment(context, {interactionId, comment}) {
    const comments = context.getters.discussionComments(interactionId);
    if (comment.parentId) comments[_.findIndex(comments, {id:comment.parentId})].replies.push(comment)
    else comments.push(comment);

    const sortedComments = _
      .chain(comments)
      .forEach(c => {
        if (c.parentId) {
          let parent = _.find(comments, (p) =>  p.id === c.parentId);
          if (parent) {
            parent.replies.push(c);
            parent.replies = _.sortBy(parent.replies, 'created');
          }
        }
      })
      .filter(c => c.parentId === null)
      .orderBy('displayTime')
      .value();
      
      const discussionInteractionHasCommentReplies = doesDiscussionInteractionHaveCommentReplies(sortedComments);
      context.commit('setDiscussionInteractionHasCommentReplies', discussionInteractionHasCommentReplies);
    Vue.set(context.state.discussionComments, interactionId, sortedComments);
  },
  deleteDummyDiscussionComment(context, {interactionId, commentId}) {
    const comments = context.getters.discussionComments(interactionId);
    comments.forEach((c, i) => {
      if (c.id === commentId) {
        comments.splice(i, 1)
        return
      }
      c.replies.forEach((r, j) => {
        if (r.id === commentId) {
          c.replies.splice(j, 1)
          return
        }
      })
    });
    const discussionInteractionHasCommentReplies = doesDiscussionInteractionHaveCommentReplies(comments);
    context.commit('setDiscussionInteractionHasCommentReplies', discussionInteractionHasCommentReplies);
    Vue.set(context.state.discussionComments, interactionId, comments);
  },
  updateDummyDiscussionComment(context, {interactionId, commentId, text}) {
    const comments = context.getters.discussionComments(interactionId);
    comments.forEach((c, i) => {
      if (c.id === commentId) c.text = text;
      c.replies.forEach((r, j) => {
        if (r.id === commentId) r.text = text;
      })
    });
    Vue.set(context.state.discussionComments, interactionId, comments);
  },
  // ####### end of dummy comment operations #######

  // ####### start of real comment actions #######
  async loadDiscussionComments(context, {sectionId, interactionId}) {
    const im = context.getters.interactionMeta(interactionId);
    try {
      im.isProcessingData = true;
      if (context.rootGetters['player/user'].readOnly) {
        const response = await PlayerService.getParentDiscussion(context.rootGetters['player/bulbAttempt'].id, interactionId);
        Vue.set(context.state.discussionComments, interactionId, response.data);
        im.isProcessingData = false;
      }
      else {
        const {  data: discussionResults } = await PlayerService.getAllDiscussionFromSection({
          interactionId,
          sectionId,
        });

        const discussionInteractionHasCommentReplies = doesDiscussionInteractionHaveCommentReplies(discussionResults);
        context.commit('setDiscussionInteractionHasCommentReplies', discussionInteractionHasCommentReplies);
        Vue.set(context.state.discussionComments, interactionId, discussionResults);
        im.isProcessingData = false;
      }
    } catch (e) {
      im.isProcessingData = false;
    }
  },
  createDiscussionComment(context, {interactionAttempt, parentId, autograde}) {
    // Index of the last required interaction of the bulb
    const lastReqIndex = _.findLastIndex(context.getters.interactionStates, is => is.interaction.isRequired && !is.interactionMeta.isInteractionAnswered && !is.interactionMeta.rubric);
    // Is the interaction the last required one of the bulb
    let syncGrades = false;
    if (
      context.rootGetters['player/queryParams'].get('type') === 'bulb' 
      && _.get(context.rootGetters['player/user'], 'gradePassbackUrl')
      && lastReqIndex >= 0
      && context.rootGetters['player/displayTime'] <= context.rootGetters['player/displayDuration'] - .2 // to prevent double syncing if interaction is at the end of the bulb
    ) {
      syncGrades = context.getters.requiredInteractionIndex === lastReqIndex;
    }
    const im = context.getters.interactionMeta(interactionAttempt.interactionId);
    im.isProcessingData = true;
    return PlayerService.createComment(interactionAttempt, parentId, autograde, syncGrades).then(response => {
      im.isProcessingData = false;
      if (response.data.interactionAttempt) {
        const interaction = _.find(context.getters.interactions, i => {
          return i.id === interactionAttempt.interactionId;
        });
        context.dispatch('handleGradedAnswer', {
          interaction,
          interactionAttempt: response.data.interactionAttempt,
        });
      }
    })
    .catch(error => {
      im.isProcessingData = false;
    });
  },
  deleteDiscussionComment(context, {interactionId, commentId}) {
    const im = context.getters.interactionMeta(interactionId)
    im.isProcessingData = true;
    return PlayerService.deleteComment(commentId)
    .catch(error => {
      if (_.has(error, 'data.response.msg')) {
        context.commit('player/setSnackbar',{
          text: error.data.response.msg,
          color: 'error',
        }, {root: true});
      } else {
        context.commit('player/setSnackbar',{
          text: 'Could not delete comment.',
          color: 'error'
        }, {root: true});
      }
      im.isProcessingData = false;
    })
  },
  updateDiscussionComment(context, {interactionId, comment}) {
    const im = context.getters.interactionMeta(interactionId);
    im.isProcessingData = true;
    return PlayerService.updateComment(comment)
    .then(response => {
      im.isProcessingData = false;
    })
    .catch(error => {
      im.isProcessingData = false;
    });
  },
  // ####### end of real comment actions #######
  
  handleSubmissionSnackbar(context, interactionMeta) {
    if (interactionMeta.isInteractionAutograded) {
      if (interactionMeta.isAnswerCorrect) {
        context.commit('player/setSnackbar',{
          text: 'Correct!',
          color: 'success'
        }, {root: true});
      } else {
        context.commit('player/setSnackbar', {
          text: 'Incorrect!',
          color: 'error'
        }, {root: true});
      }
    } else {
      const interaction = context.getters.interaction(interactionMeta.interactionId);
      const type = interaction && interaction.interactionType ? interaction.interactionType : '';
      // BARBRI wants to remove the 'Progress Saved' toast on pause and iFrame interactions.
      if ([883, 1470, 2233, 2234, 2235, 2611].includes(_.get(context, 'rootState.player.bulb.teachers[0].school.id', 0))) {
        if (!['pause_q', 'iframe_q'].includes(type)) {
          context.commit('player/setSnackbar', {
            text: 'Submitted!'
          }, {root: true});
        }
      } else {
        if (
          type !== 'pause_q'
          || (
            type === 'pause_q'
            && !_.get(interaction, 'interactionText', '').includes('data-pp-jump-instant')
          )
        ) {
          context.commit('player/setSnackbar', {
            text: ['pause_q', 'iframe_q'].includes(type) ? 'Progress Saved.' : 'Submitted!'
          }, {root: true});
        }
      }
    }
    if (interactionMeta.attempts > 1 && context.rootGetters['player/user'].isLti && context.rootGetters['player/user'].gradePassbackUrl && context.rootGetters['player/bulbAttempt'].complete) {
      context.commit('player/setSnackbar', {
        text: 'Interaction Graded, click to resync LMS grade.',
        color: 'warning',
        action: 'sync',
        timeout: 0,
      }, {root: true})
    }
  },
  loadPreviousInteractionAttempts(context, bulbAttemptId) {
    PlayerService.getInteractionAttemptCount(bulbAttemptId)
    .then(res => {
      res.data.forEach(a => {
        context.getters.interactionMeta(a.interactionId).attempts = a.attempts;
      })
    })
    .catch(err => {
      if (_.has(err, 'response.data.err') && err.response.data.err === 'archived_bulb_attempt') {
         context.dispatch('failedAJAXToast', err.response.data.err)
      } else {
        dataLayer.errorLog(err, {
          label_data_keys: ['err'],
          message_data_keys: ['err']
       })
      }
    })
  },
  submitAnswer: async function(context, { interaction, interactionAttempt }) {
    // Index of the last required interaction of the bulb
    const lastReqIndex = _.findLastIndex(context.getters.interactionStates, is => is.interaction.isRequired && !is.interactionMeta.isInteractionAnswered && !is.interactionMeta.rubric)
    // Is the interaction the last required one of the bulb
    let syncGrades = false;
    if (context.rootGetters['player/queryParams'].get('type') === 'bulb'
      && _.get(context.rootGetters['player/user'], 'gradePassbackUrl')
      && _.get(context.rootGetters['player/user'], 'enableGrading') === 1
      && lastReqIndex >= 0
      && context.rootGetters['player/displayTime'] <= context.rootGetters['player/displayDuration'] - .2 // to prevent double syncing if interaction is at the end of the bulb
    ) {
      syncGrades = context.getters.requiredInteractionIndex === lastReqIndex;
    }
    if (context.rootGetters['interactions/interactionMeta'](interaction.id).preventResubmit) {
      context.dispatch('failedAJAXToast', context.rootGetters['interactions/interactionMeta'](interaction.id).preventResubmit)
      return
    }
    if(context.state.editFreeResponseState){
      context.commit('appendEditFreeResponse', interactionAttempt.interactionId);
    }
    context.rootGetters['interactions/interactionMeta'](interaction.id).isProcessingData = true;
    // set time elapsed
    const im = context.rootGetters['interactions/interactionMeta'](interaction.id)
    const sessionStartTime = im.sessionStartTime;
    const tempSessionTime = im.tempSessionTime;
    interactionAttempt.timeElapsed = moment.duration(moment().diff(sessionStartTime)).asSeconds() + tempSessionTime;
    let sanitizedValue;
    if (!im.useRichText && interaction.interactionType === 'free_response') {
      sanitizedValue = interactionAttempt.responseText[0].replace(/</g,'&lt;'); // Input is inserted into a div via v-html - this escapes the < so it correctly displays
    }
    
    context.dispatch('constructAnswerSubmission', {
      interaction,
      interactionAttempt: (
        (
          interaction.interactionType === 'free_response'
          && sanitizedValue
        ) ? { ...interactionAttempt, ...{ responseText: [sanitizedValue] } } : interactionAttempt
      ),
      interactionMeta: context.rootGetters['interactions/interactionMeta'](interaction.id), displayTime: context.rootGetters['player/displayTime']
    })
    .then(submission => {
      const user = context.rootGetters['player/user'];
      submission.syncGrades = syncGrades;
      submission.lti13Launch = !!context.rootState.player.lti13Launch;
      submission.issuer = context.rootState.player?.lmsIssuer;
      submission.scoreThreshold = (context.rootGetters['player/bulb'].config?.scoreThreshold);
      const gradeOnly = (
        !user
        || user.userType !== 'student'
      );
      PlayerService.submitAnswer(submission, gradeOnly)
      .then(response => {
        context.rootGetters['interactions/interactionMeta'](interaction.id).isProcessingData = false;
        if (response.data.interaction && response.data.interactionAttempt) {
          context.dispatch('handleGradedAnswer', {
            interaction: response.data.interaction,
            interactionAttempt: response.data.interactionAttempt,
          })
          .then(graded => {
            EventBus.$emit('buildSubmitQuestionStatement', graded.interaction.id); // xapi call
            EventBus.$emit('finishedSubmittingAnswer', graded.interactionAttempt); // Fill in the Blank reset
            if (
              _.get(graded, 'interaction.config.hide_correct_answer_always', false)
              || (
                _.get(graded, 'interaction.config.show_highlighting_last_attempt_only', false)
                && submission.interaction.attempts > submission.interactionMeta.attempts
              )
            ) {
              context.commit('player/setSnackbar', {
                text: 'Submitted!'
              }, {root: true});
            } else {
              context.dispatch('handleSubmissionSnackbar', context.rootGetters['interactions/interactionMeta'](graded.interaction.id));
            }
            if (context.rootGetters['player/hasNewInteractionDisplayAccess']) context.commit('setInteractionStates');
            if(context.state.editFreeResponseState){
              context.commit('setEditFreeResponse')
              context.commit('appendEditFreeResponse', interactionAttempt.interactionId);
            }
          })
          .catch(err => {
            // handleGradedAnswer error
            context.commit('player/setSnackbar', {
              text: 'Error while processing the submitted interaction attempt.',
              color: 'error'
            }, { root: true })
          })
        } else {
          dataLayer.errorLog({msg: `${JSON.parse(response.data)}, sessionId: ${context.rootState.player.sessionId}`, location: 'Unable to submit interactionAttempt'})
          // no graded interaction returned error
          context.commit('player/setSnackbar', {
            text: 'Unable to submit this interaction attempt.',
            color: 'error'
          }, { root: true })
        }
      })
      .catch(err => {
        // axios submitAnswer error
        context.rootGetters['interactions/interactionMeta'](interaction.id).isProcessingData = false;
        if (_.has(err, 'response.data.error')) {
          context.rootGetters['interactions/interactionMeta'](interaction.id).preventResubmit = err.response.data.error;
          context.dispatch('failedAJAXToast', err.response.data.error)
        } else {
          context.rootGetters['interactions/interactionMeta'](interaction.id).preventResubmit = 'err';
          if (_.get(err, 'response.data.message') === 'archived_interaction') {
            context.dispatch('failedAJAXToast', err.response.data.message);
          } else {
            context.commit('player/setSnackbar', {
              text: 'Unable to submit this interaction attempt.',
              color: 'error'
            }, { root: true });
          }
        }
      });
    })
    .catch(err => {
      // constuctAnswerSubmissionError (should never happen)
      context.rootGetters['interactions/interactionMeta'](interaction.id).isProcessingData = false;
      dataLayer.errorLog({msg:err.message, location: 'submitAnswer:constructAnswerSubmission'});
      context.commit('player/setSnackbar', {
        text: 'Unable to grade this interaction attempt.',
        color: 'error'
      }, { root: true })
    })
  },
  submitRubric(context, rubricInteractionStates) {
    context.commit('setRubricSubmitting', true);
    const gradeOnly = !context.rootGetters['player/user'].id || !context.rootGetters['player/bulbAttempt'].id;
    const lti13Launch = context?.rootState?.player?.lti13Launch;
    const issuer = context.rootState.player?.lmsIssuer;
    const data = {
      gradeOnly,
      lti13Launch,
      issuer,
    };
    PlayerService.submitRubric(rubricInteractionStates, data)
    .then(response => {
      context.commit('setRubricSubmitting', false);
      const handleGradedPromises = [];
      response.data.forEach(is => {
        handleGradedPromises.push(
          context.dispatch('handleGradedAnswer', {
            interaction: is.interaction,
            interactionAttempt: is.interactionAttempt,
          })
        )
      })
      Promise.all(handleGradedPromises)
      .then(r => {
        if (context.rootGetters['player/user'].isTeacherLmbAttempt) {
          context.commit('player/setBulbAttempt', { complete: 1 }, { root: true });
        }
        context.commit('setRubricSubmitted', context.rootGetters['player/displayTime']);
        if (!context.rootGetters['player/bulbFinished']) { 
          // this prevents double firing toasts for Bulb Complete and Rubric Submitted for VoiceOver
          context.commit('player/setSnackbar', {
            text: gradeOnly ? 'Preview Mode: Answers and progress are not saved.' : 'Rubric Exported Successfully.',
            ...!gradeOnly && { color: 'success' }
          }, { root: true })
        }
      })
      .catch(err => dataLayer.errorLog({msg:err.message, location: 'submitRubric: handleGradedPromises'}));
    })
    .catch(err => {
      // catch for submitting Rubric
      const errData    = err.response.data;
      const ltiErrData = err.response.data?.ltiErr;
      let errToast = {
        text: ltiErrData?.msg || errData.msg || 'Error Submitting Rubric',
        color: (() => {
          if (
            errData?.msg?.includes('Grading is not enabled for this assignment')
            || ltiErrData?.msg?.includes('Grading is not enabled for this assignment')
          ) {
            return 'warning';
          }
          return 'error';
        })(),
      };
      if (errData.name) {
        if (errData.name === 'SequelizeUniqueConstraintError') {
          context.commit('setRubricSubmitted', context.rootGetters['player/displayTime']);
          errToast = { text: 'Rubric has already been submitted.', color: 'warning'}
        }
        else {
          errToast = {
            text: errData.msg,
            color: 'warning',
          };
        }
      }
      dataLayer.errorLog({
        msg:err.message,
        location: 'submitRubric: ajax',
      });

      context.commit('setRubricSubmitting', false);
      context.commit('player/setSnackbar', errToast, { root: true })
    });
  },
  getInteractionAnalytics: async function(context, {
    interactionId,
    sectionId,
    cache = true,
  } = {}) {
    try {
      const res = await PlayerService.getInteractionAnalytics({
        interactionId,
        sectionId,
        cache,
        token: context.rootGetters['player/user'].token,
      });
      return res.data;
    } catch (err) {
      dataLayer.errorLog({msg:`${err.message} sectionId: ${sectionId} interactionId: ${interactionId}`, location: 'getInteractionAnalytics'});
    }
  },
  failedAJAXToast(context, errorCode) {
    if (errorCode === 'archived_bulb_attempt') {
       context.commit('player/setSnackbar', {
        text: `This ${context.getters.termForBulb} attempt was archived.`,
        color: 'error',
        bluescreen: {
          title: `Archived ${context.getters.termForBulb.capitalize()} Attempt`,
          subtitle: `You or your instructor has reset this ${context.getters.termForBulb} attempt, so you cannot submit any answers.`,
          code: '110',
          text: 'Please relaunch the assignment to start a new attempt.',
          location: `InteractionSumitAnswer`,
        },
        timeout: 0,
      }, { root: true });
    }
    if (errorCode === 'archived_interaction') {
      context.commit('player/setSnackbar', {
        text: 'This interaction was deleted.',
        color: 'error',
        bluescreen: {
          title: 'Archived Interaction',
          subtitle: 'Your instructor has deleted this interaction.',
          code: '112',
          text: `Please relaunch the assignment to get the most recent version of this ${context.getters.termForBulb}.`,
          location: `InteractionSumitAnswer`,
        },
        timeout: 0,
      }, { root: true });
    }
    if (errorCode === 'duplicate_entry') {
      context.commit('player/setSnackbar', {
        text: 'This interaction has already been submitted. Please refresh the page.',
        color: 'error',
        timeout: 5000,
        bluescreen: {
          title: 'Duplicate Submission',
          subtitle: 'You already submitted an answer for this interaction.',
          code: '111',
          text: 'Please relaunch the assignment to view your current answer.',
          location: `InteractionSumitAnswer`,
          // msg: `You already submitted an answer for this interaction. Please relaunch the assignment to view your current answer. [Code 111]`,
          // noButtons: true,
        },
      }, { root: true });
    }
  },
  createAttachmentDownloadLog(context, data) {
    try {
      PlayerService.createAttachmentDownloadLog(data);
    } catch(error) {
      throw error;
    }
  },
  continueOnward({ getters }, { interactionMeta, nextCallback, continueCallback }) {
    if (getters.continueAction({ interactionMeta }) === 'next') {
      if (nextCallback) nextCallback();
    } else {
      if (continueCallback) continueCallback();
    }
  },
}

